import { X } from "phosphor-react";
import { Dialog } from "@headlessui/react";
import { useRef } from "react";
import clsx from "clsx";

type DialogBoxProps = {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    subtitle?: string;
    description?: string | string[];
    children?: React.ReactNode;
    size?: "xl" | "large" | "medium" | "small";
    closeButton?: boolean;
};

const DialogBox: React.FC<DialogBoxProps> = ({
    title,
    subtitle,
    description,
    isOpen,
    onClose,
    children,
    size = "large",
    closeButton = false,
}: DialogBoxProps) => {
    const closeButtonRef = useRef(null);
    return (
        <Dialog
            as="div"
            className="relative z-50"
            open={isOpen}
            onClose={onClose}
            initialFocus={closeButtonRef}
        >
            <div
                className="fixed inset-0 bg-S2D-neutral-50/75"
                aria-hidden="true"
            />
            <div className="fixed inset-0 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center text-center">
                    <Dialog.Panel
                        data-testid={
                            title
                                ? `${title
                                      .toLowerCase()
                                      .replace(/\s+/g, "-")}-dialog`
                                : "dialog-box"
                        }
                        className={clsx(
                            "w-full transform rounded-sm rounded-b-sm bg-white text-left align-middle shadow-xl transition-all",
                            size === "xl" &&
                                "max-w-full sm:max-w-lg md:max-w-2xl lg:max-w-3xl",
                            size === "large" && "max-w-lg",
                            size === "medium" && "max-w-sm",
                            size === "small" && "max-w-xs"
                        )}
                    >
                        {closeButton && (
                            <X
                                className="float-right m-4 h-6 w-6 cursor-pointer"
                                onClick={onClose}
                                ref={closeButtonRef}
                                data-testid="dialog-close-button"
                            />
                        )}
                        {title && (
                            <Dialog.Title className="mb-3 break-words px-6 pt-6 text-xl font-semibold">
                                {title}
                            </Dialog.Title>
                        )}
                        {subtitle && (
                            <Dialog.Title className="text-large overflow-wrap mb-3 break-words px-6 font-semibold">
                                {subtitle}
                            </Dialog.Title>
                        )}

                        {typeof description === "string" ? (
                            <Dialog.Description className="mb-8 px-6 text-sm">
                                {description}
                            </Dialog.Description>
                        ) : null}

                        {/* If description is array (type object), map out each string as separate text block. */}
                        {typeof description === "object"
                            ? description.map((str) => (
                                  <Dialog.Description
                                      key={str}
                                      className="mb-8 px-6 text-sm"
                                  >
                                      {str}
                                  </Dialog.Description>
                              ))
                            : null}
                        {children}
                    </Dialog.Panel>
                </div>
            </div>
        </Dialog>
    );
};

export default DialogBox;
